import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share, ArrowLeft } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../../services/api';

const PostDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPost();
    }
  }, [id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.get(`/posts/public/${id}`);
      if (resp.data?.success) {
        setPost(resp.data.data.post);
      } else {
        setError('Post not found');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) setError('This post is not publicly available');
      else if (status === 404) setError('Post not found');
      else setError('Failed to load post. Please try again.');
      console.error('Failed to load post:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Player': return 'bg-blue-100 text-blue-800';
      case 'Coach': return 'bg-green-100 text-green-800';
      case 'Organization': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800 font-bold';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-red-100 text-red-800',
      'Tennis': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Cricket': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    if (!post) return;

    const shareUrl = window.location.href;
    const shareData = {
      title: `${post.user.name} shared a post`,
      text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
      url: shareUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Post shared successfully!');
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading post..." />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The post you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Post</h1>
        </div>
      </div>

      {/* Post Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Post Header */}
          <div className="p-6 bg-gradient-to-r from-gray-50 to-white flex items-start justify-between gap-4 border-b border-gray-100">
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                {post.user.avatar ? (
                  <img 
                    src={post.user.avatar} 
                    alt={post.user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium">
                    {getInitials(post.user.name)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2 className="font-semibold text-base text-gray-900 truncate">
                    {post.user.name}
                  </h2>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(post.user.role)} whitespace-nowrap`}>
                    {post.isAdminPost ? 'ADMIN' : post.user.role}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(post.user.sport)} whitespace-nowrap`}>
                    {post.user.sport}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {formatDate(post.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div className="px-6 py-4">
            <p className="text-lg text-gray-900 break-words leading-relaxed whitespace-pre-wrap">
              {post.content}
            </p>
          </div>

          {/* Post Image */}
          {post.image && (
            <div className="px-6 pb-4">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={post.image}
                  alt="Post content"
                  className="w-full h-auto max-h-96 object-contain rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Post Video */}
          {post.video && (
            <div className="px-6 pb-4">
              <div className="relative overflow-hidden rounded-xl">
                <video
                  src={post.video.url}
                  controls
                  poster={post.video.thumbnail || undefined}
                  className="w-full h-auto max-h-96 object-contain rounded-xl"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}

          {/* Post Stats */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-600 font-medium">
              <span>{post.likes} {post.likes === 1 ? 'like' : 'likes'}</span>
              <div className="flex space-x-4">
                <span>{post.comments} {post.comments === 1 ? 'comment' : 'comments'}</span>
                <span>{post.shares} {post.shares === 1 ? 'share' : 'shares'}</span>
              </div>
            </div>
          </div>

          {/* Post Actions */}
          <div className="px-6 py-3 border-t border-gray-100 bg-white">
            <div className="flex items-center justify-around gap-2">
              <button
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                disabled
              >
                <Heart className="w-5 h-5" />
                <span className="text-sm font-medium">Like</span>
              </button>
              <button
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                disabled
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Comment</span>
              </button>
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
              >
                <Share className="w-5 h-5" />
                <span className="text-sm font-medium">Share</span>
              </button>
            </div>
          </div>

          {/* Call to Action */}
          <div className="px-6 py-4 bg-blue-50 border-t border-gray-100">
            <p className="text-sm text-gray-700 text-center mb-3">
              Want to join the conversation?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/signup')}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                Sign Up
              </button>
              <button
                onClick={() => navigate('/login')}
                className="flex-1 px-4 py-2 bg-white text-blue-500 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;

